// App-level host for the single, persistent Chat module instance (see persistentChatsStore.js).
// Mounted ONCE in App.js inside the Router and outside <Routes>, so route changes never
// unmount it. It mounts WorkspaceChats only while it is needed:
//   • the Chats route is on screen, OR
//   • a call / huddle is in progress (WorkspaceChats publishes keep-alive),
// and only while the user is authenticated — logout unmounts it, which ends the call.
import React, { Suspense, useCallback, useContext, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import lazyWithRetry from "../../utils/lazyWithRetry";
import CommunicationBadgeContext from "../../context/CommunicationBadgeContext";
import RouteErrorBoundary from "../RouteErrorBoundary";
import { authService } from "../../utils/auth";
import {
  ChatsHostContext,
  getHostContainer,
  isCallWindowSuppressedPath,
  usePersistentChatsState,
} from "./persistentChatsStore";

const WorkspaceChats = lazyWithRetry(() => import("../../pages/workspace/modules/WorkspaceChats"));

function ChatsLoading() {
  return (
    <div className="flex-1 flex items-center justify-center min-h-[200px]" role="status" aria-label="Loading chats">
      <div className="w-8 h-8 rounded-full border-2 border-[#033F99]/20 border-t-[#033F99] animate-spin" />
    </div>
  );
}

export default function PersistentChatsHost({ isAuthenticated, component }) {
  const { slotEl, keepAlive, badgeValue } = usePersistentChatsState();
  const location = useLocation();
  const navigate = useNavigate();
  const fallbackBadges = useContext(CommunicationBadgeContext);

  // Stable navigate so the hosted module does not re-render on every location change.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const navigateTo = useCallback((to) => navigateRef.current(to), []);

  const parked = !slotEl;
  const suppressCallWindow = isCallWindowSuppressedPath(location.pathname);
  const hostValue = useMemo(
    () => ({ hosted: true, parked, suppressCallWindow, navigateTo }),
    [parked, suppressCallWindow, navigateTo]
  );

  // A merely-expired access token (refresh pending) must not drop an active call, so stay
  // mounted while a session still exists. Explicit logout clears token + user (and broadcasts
  // AUTH_LOGOUT, on which WorkspaceChats ends the call), so this then goes false and unmounts.
  const sessionAlive = Boolean(isAuthenticated) || authService.hasRefreshToken();
  const shouldMount = sessionAlive && (Boolean(slotEl) || keepAlive);
  const container = getHostContainer();
  if (!shouldMount || !container) return null;

  const Component = component || WorkspaceChats;

  // The tree shape below must stay identical across renders (providers always present), or
  // React would remount WorkspaceChats and drop the call.
  return createPortal(
    <CommunicationBadgeContext.Provider value={badgeValue || fallbackBadges}>
      <ChatsHostContext.Provider value={hostValue}>
        <RouteErrorBoundary>
          <Suspense fallback={parked ? null : <ChatsLoading />}>
            <Component />
          </Suspense>
        </RouteErrorBoundary>
      </ChatsHostContext.Provider>
    </CommunicationBadgeContext.Provider>,
    container
  );
}
